/**
 * Delivery date — evergreen model.
 *
 * window.StockedDelivery: shared logic for the cart-level delivery date.
 *   - Dates are generated by rule (selectableDates(): Sun/Mon/Tue × lookahead weeks),
 *     computed in store time. No per-date metaobjects required.
 *   - One date per order, stored in localStorage and mirrored to cart attributes
 *     (delivery_date / _label / _timestamp / _cutoff). The cart attribute drives the
 *     server-rendered checkout gate and carries the date onto the order for fulfilment.
 *   - On the first add, product-form.js calls requireSelection() which opens the date
 *     drawer so the customer picks a date before the item is added; the inline cart
 *     calendar lets them change it later.
 *
 * <cart-delivery-date>: the month-grid calendar UI, built on the module.
 *
 * Vanilla — no external dependencies.
 */

(function () {
  const STORAGE_KEY = 'stockedDeliveryDate';
  const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  /* ---------------------------------------------------- schedule rule config */

  // The store's timezone — ALL "today / cutoff" math is done in this zone so the
  // customer's browser timezone never changes which dates are offered.
  const DELIVERY_TZ = 'America/New_York';

  // Delivery rules (weekdays, blackouts, lookahead, per-date cutoff days-before + overrides)
  // come from the shared stocked.delivery_config metafield, injected by Liquid as
  // window.stockedDeliveryConfig. The validation function reads the same metafield, so the
  // calendar and the checkout gate can't disagree. The defaults below are guardrails for a
  // missing/malformed field. (The cutoff TIME of 12:30 is intentionally NOT here — it's
  // hardcoded in deliveryCutoff() and the function's input-query literal; change one, change both.)
  function config() {
    var c = window.stockedDeliveryConfig;
    if (typeof c === 'string') {
      try { c = JSON.parse(c); } catch (e) { c = null; }
    }
    return c && typeof c === 'object' ? c : {};
  }

  // Stocked delivers on these weekdays (0 = Sun, 1 = Mon, 2 = Tue) — from config.
  function deliveryWeekdays() {
    var w = config().delivery_weekdays;
    return Array.isArray(w) && w.length ? w : [0, 1, 2];
  }

  // Per-date cutoff inputs from config: `daysBefore` (days before EACH delivery date; the
  // 12:30 time is hardcoded in deliveryCutoff()) and `overrides` (deliveryISO -> cutoff date).
  function cutoffConfig() {
    var c = config();
    var n = c.cutoff_days_before_delivery;
    var o = c.cutoff_overrides;
    return {
      daysBefore: Number.isInteger(n) ? n : 4,
      overrides: (o && typeof o === 'object' && !Array.isArray(o)) ? o : {},
    };
  }

  /* ----------------------------------------------------------- date helpers */

  function lookaheadWeeks() {
    return parseInt(config().lookahead_weeks, 10) || 3;
  }

  // Current wall-clock in the store timezone, as plain numbers (no browser-TZ drift).
  function nowET() {
    const parts = {};
    new Intl.DateTimeFormat('en-US', {
      timeZone: DELIVERY_TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
      .formatToParts(new Date())
      .forEach((p) => {
        if (p.type !== 'literal') parts[p.type] = p.value;
      });
    let hour = parseInt(parts.hour, 10);
    if (hour === 24) hour = 0; // some engines emit '24' at midnight
    return { year: +parts.year, month: +parts.month, day: +parts.day, hour, minute: +parts.minute };
  }

  // Weekday of a calendar date (0 = Sun). UTC construction → no DST/TZ ambiguity.
  function weekdayOf(y, m, d) {
    return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  }

  // Shift a calendar date by n days using UTC math (DST-safe), returns {y,m,d}.
  function addDays(y, m, d, n) {
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + n);
    return { y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
  }

  function ymdToInt(y, m, d) {
    return y * 10000 + m * 100 + d;
  }

  function isoOf(y, m, d) {
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }

  // A local Date built from calendar components — getDay()/getMonth()/toLocaleDateString
  // all reflect those exact components regardless of browser timezone.
  function localDate(y, m, d) {
    return new Date(y, m - 1, d, 12, 0, 0, 0);
  }

  function formatLong(day) {
    return day.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  function toISO(year, month, day) {
    return isoOf(year, month + 1, day); // month here is 0-based (calendar grid)
  }

  // Holiday / non-delivery dates (ISO strings) from the delivery_blackout metaobject.
  function blackoutSet() {
    const list = Array.isArray(config().blackouts) ? config().blackouts : [];
    return new Set(list.map((v) => String(v).slice(0, 10)));
  }

  function cutoffPassed(y, m, d, now) {
    const c = window.StockedDeliveryFormat.deliveryCutoff(y, m, d, cutoffConfig());
    // Compare down to the minute (cutoff is 12:30, not on an hour boundary).
    const cutoffInt = ymdToInt(c.y, c.m, c.d) * 10000 + c.hour * 100 + c.minute;
    const nowInt = ymdToInt(now.year, now.month, now.day) * 10000 + now.hour * 100 + now.minute;
    return nowInt >= cutoffInt;
  }

  /* -------------------------------------------------------- date generation */

  // Generates the next N selectable delivery dates by rule (N = lookaheadWeeks ×
  // delivery-days-per-week), skipping blackouts and past-cutoff days. Count-based (not a
  // fixed window) so a mid-week "today" or an early cutoff doesn't clip the last week —
  // the customer always gets a full N dates to choose from. No dependency on weekly_menu
  // or the capped delivery_date metaobject list.
  function selectableDates() {
    const now = nowET();
    const weekdays = deliveryWeekdays();
    const target = lookaheadWeeks() * weekdays.length;
    const blackouts = blackoutSet();
    const out = [];
    for (let i = 0; i <= 366 && out.length < target; i += 1) {
      const { y, m, d } = addDays(now.year, now.month, now.day, i);
      if (!weekdays.includes(weekdayOf(y, m, d))) continue;
      const iso = isoOf(y, m, d);
      if (blackouts.has(iso)) continue;
      if (cutoffPassed(y, m, d, now)) continue;
      out.push({ iso, y, m, d, day: localDate(y, m, d) });
    }
    return out;
  }

  function findDate(iso) {
    return selectableDates().find((e) => e.iso === iso) || null;
  }

  function toSelection(entry) {
    const c = window.StockedDeliveryFormat.deliveryCutoff(entry.y, entry.m, entry.d, cutoffConfig());
    return {
      iso: entry.iso,
      // Generated dates have no metaobject id; use the ISO date as a stable handle/id.
      id: entry.iso,
      handle: entry.iso,
      label: formatLong(entry.day),
      timestamp: Math.floor(entry.day.getTime() / 1000),
      cutoffTimestamp: Math.floor(localDate(c.y, c.m, c.d).setHours(c.hour, c.minute) / 1000),
    };
  }

  /* ----------------------------------------------------------- localStorage */

  function getStored() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function setStored(sel) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(sel));
    } catch (e) {
      /* ignore */
    }
  }

  function clearStored() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* ignore */
    }
  }

  // Current valid selection, or null if none / stale.
  function getSelection() {
    const s = getStored();
    return s && findDate(s.iso) ? s : null;
  }

  function select(iso) {
    const entry = findDate(iso);
    if (!entry) return null;
    const sel = toSelection(entry);
    setStored(sel);
    return sel;
  }

  /* --------------------------------------------------- cart writes (attrs) */

  function toAttributes(sel) {
    var weekCode = '';
    var windowLabel = '';
    var dueLabel = '';
    var fmt = window.StockedDeliveryFormat;
    if (sel && sel.iso && fmt) {
      var p = sel.iso.split('-');
      var y = parseInt(p[0], 10);
      var m = parseInt(p[1], 10);
      var d = parseInt(p[2], 10);
      weekCode = fmt.deliveryWeekCode(y, m, d);
      windowLabel = fmt.deliveryWindowLabel(y, m, d);
      dueLabel = fmt.deliveryDueLabel(y, m, d);
    }
    return {
      delivery_date: sel ? sel.iso : '',
      delivery_date_label: sel ? sel.label : '',
      delivery_date_timestamp: sel && sel.timestamp ? String(sel.timestamp) : '',
      delivery_date_cutoff: sel && sel.cutoffTimestamp ? String(sel.cutoffTimestamp) : '',
      delivery_week_code: weekCode,
      delivery_window_label: windowLabel,
      'Order Due Date': dueLabel,
    };
  }

  function cartUpdateUrl() {
    return (window.routes && window.routes.cart_update_url) || '/cart/update.js';
  }

  function ajax(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    });
  }

  async function setCartAttributes(sel) {
    try {
      await ajax(cartUpdateUrl(), { attributes: toAttributes(sel) });
    } catch (e) {
      console.error('StockedDelivery: setCartAttributes failed', e);
    }
  }

  // True when the customer must still pick a date (and dates are available).
  function needsSelection() {
    return !getSelection() && selectableDates().length > 0;
  }

  // selectableDates() limited to those on/before maxISO (all of them when no max).
  function selectableWithin(maxISO) {
    var list = selectableDates();
    if (!maxISO) return list;
    return list.filter(function (e) { return e.iso <= maxISO; });
  }

  // Used by product-form on add: resolves to the selection, opening the modal
  // calendar first if none is chosen (or the stored one violates opts.maxDate).
  // Resolves null if there are no offerable dates within maxDate, or the customer
  // dismisses the modal without choosing.
  function requireSelection(opts) {
    opts = opts || {};
    var maxISO = opts.maxDate || null;
    var sel = getSelection();
    if (sel && (!maxISO || sel.iso <= maxISO)) return Promise.resolve(sel);
    if (!selectableWithin(maxISO).length) return Promise.resolve(null);
    if (api._modal && typeof api._modal.open === 'function') {
      return api._modal.open({ maxDate: maxISO }).then(function (result) {
        // A dismissed constrained modal defaults to the stored selection; if that
        // stored date is out of range, treat it as no-selection so the add aborts
        // instead of proceeding with a too-late date.
        return (result && (!maxISO || result.iso <= maxISO)) ? result : null;
      });
    }
    return Promise.resolve(null);
  }

  // Shows the Specials conflict popup; resolves 'change' (re-pick) or 'cancel'.
  // If the popup markup is missing, resolve 'change' so the customer can still re-pick.
  function promptSpecialConflict() {
    var el = document.getElementById('StockedSpecialDatePopup');
    if (!el) return Promise.resolve('change');
    el.hidden = false;
    document.body.classList.add('overflow-hidden');
    requestAnimationFrame(function () { el.classList.add('is-active'); });
    return new Promise(function (resolve) {
      function cleanup(result) {
        el.classList.remove('is-active');
        document.body.classList.remove('overflow-hidden');
        el.hidden = true;
        el.removeEventListener('click', onClick);
        resolve(result);
      }
      function onClick(e) {
        if (e.target.closest('[data-special-change]')) cleanup('change');
        else if (e.target.closest('[data-special-cancel], [data-special-overlay]')) cleanup('cancel');
      }
      el.addEventListener('click', onClick);
    });
  }

  const api = {
    selectableDates,
    findDate,
    getSelection,
    select,
    needsSelection,
    requireSelection,
    promptSpecialConflict,
    toAttributes,
    setCartAttributes,
    clearStored,
    formatLong,
    WEEKDAYS,
    MONTHS,
    _modal: null,
  };
  window.StockedDelivery = api;

  /* ----------------------------------------------------- <cart-delivery-date> */

  class CartDeliveryDate extends HTMLElement {
    connectedCallback() {
      this.maxDate = this.dataset.maxDate || null;
      this.mode = this.dataset.mode || 'inline';
      this.calendarEl = this.querySelector('[data-delivery-calendar]');
      this.summaryEl = this.querySelector('[data-delivery-summary]');
      if (!this.calendarEl || !this.summaryEl) return;

      this.refreshDates();

      // Reconcile server attribute (cart) with localStorage.
      const serverDate = this.dataset.selectedDate || null;
      if (serverDate && this.dates.has(serverDate)) {
        this.selectedDate = serverDate;
        if (this.mode !== 'modal') {
          const stored = getStored();
          if (!stored || stored.iso !== serverDate) select(serverDate);
        }
      } else {
        const stored = getSelection();
        if (stored) {
          this.selectedDate = stored.iso;
          // Inline (cart) surfaces push the stored date to the cart attribute if it
          // wasn't written yet (e.g. immediately after add).
          if (this.mode !== 'modal') {
            setCartAttributes(stored);
          }
        } else {
          this.selectedDate = null;
        }
      }

      this.viewMonth = this.initialMonth();

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);

      if (this.mode === 'modal') {
        api._modal = this;
        this.hidden = true;
      } else {
        this.expanded = !this.selectedDate || !this.isWithinMax(this.selectedDate);
        this.render();
        this.syncCheckoutButton();
      }
    }

    isWithinMax(iso) {
      return !this.maxDate || iso <= this.maxDate;
    }

    refreshDates() {
      this.dates = new Map(selectableDates().map((e) => [e.iso, e]));
      this.selectableList = Array.from(this.dates.keys())
        .filter((iso) => this.isWithinMax(iso))
        .sort();
    }

    /* ------------------------------------------------------------ modal API */

    open(opts) {
      opts = opts || {};
      this.maxDate = opts.maxDate || null;
      this.refreshDates();
      this.expanded = true;
      this.hidden = false;
      document.body.classList.add('overflow-hidden');
      this.render();
      // Add the active class on the next frame so the drawer slides in (transition).
      requestAnimationFrame(() => this.classList.add('is-active'));
      const focusTarget =
        this.calendarEl.querySelector('[data-delivery-day][tabindex="0"]') ||
        this.calendarEl.querySelector('[data-delivery-day]');
      if (focusTarget) focusTarget.focus();
      return new Promise((resolve) => {
        this._resolveOpen = resolve;
      });
    }

    close(result) {
      this.classList.remove('is-active');
      document.body.classList.remove('overflow-hidden');
      // Let the slide-out transition play before hiding.
      const hide = () => {
        this.hidden = true;
        this.removeEventListener('transitionend', hide);
      };
      const dialog = this.querySelector('.delivery-date-modal__dialog');
      if (dialog) {
        this.addEventListener('transitionend', hide, { once: true });
        setTimeout(hide, 400); // fallback if transitionend doesn't fire
      } else {
        this.hidden = true;
      }
      if (this._resolveOpen) {
        this._resolveOpen(result !== undefined ? result : getSelection());
        this._resolveOpen = null;
      }
      this.maxDate = null;
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
    }

    initialMonth() {
      const anchor =
        (this.selectedDate && this.dates.get(this.selectedDate)?.day) ||
        (this.selectableList.length && this.dates.get(this.selectableList[0]).day) ||
        new Date();
      return { year: anchor.getFullYear(), month: anchor.getMonth() };
    }

    monthKey(year, month) {
      return year * 12 + month;
    }

    monthBounds() {
      if (!this.selectableList.length) {
        const now = new Date();
        const key = this.monthKey(now.getFullYear(), now.getMonth());
        return { min: key, max: key };
      }
      const first = this.dates.get(this.selectableList[0]).day;
      const last = this.dates.get(this.selectableList[this.selectableList.length - 1]).day;
      return {
        min: this.monthKey(first.getFullYear(), first.getMonth()),
        max: this.monthKey(last.getFullYear(), last.getMonth()),
      };
    }

    /* ---------------------------------------------------------------- render */

    render() {
      this.renderSummary();
      if (this.expanded) {
        this.calendarEl.hidden = false;
        this.renderCalendar();
      } else {
        this.calendarEl.hidden = true;
        this.calendarEl.innerHTML = '';
      }
    }

    renderSummary() {
      if (this.selectedDate && this.isWithinMax(this.selectedDate)) {
        const label = this.dates.get(this.selectedDate)
          ? formatLong(this.dates.get(this.selectedDate).day)
          : this.dataset.selectedLabel || this.selectedDate;
        this.summaryEl.innerHTML =
          `<span class="cart-delivery-date__label">Delivering on <strong>${label}</strong></span>` +
          `<button type="button" class="cart-delivery-date__change link" data-delivery-action="toggle">` +
          `${this.expanded ? 'Done' : 'Change'}</button>`;
      } else if (this.maxDate) {
        // A Special in the cart caps the latest delivery date. Explain why a date is
        // needed (or why the previously chosen one is no longer valid) instead of a
        // bare prompt, so the blocked checkout button has a visible reason.
        const p = this.maxDate.split('-');
        const maxLabel = formatLong(localDate(+p[0], +p[1], +p[2]));
        this.summaryEl.innerHTML =
          `<span class="cart-delivery-date__label cart-delivery-date__label--prompt cart-delivery-date__label--warning">` +
          `An item in your cart must be delivered by <strong>${maxLabel}</strong>. Please choose an earlier delivery date.</span>`;
      } else {
        this.summaryEl.innerHTML =
          `<span class="cart-delivery-date__label cart-delivery-date__label--prompt">Choose a delivery date</span>`;
      }
    }

    renderCalendar() {
      const { year, month } = this.viewMonth;
      const bounds = this.monthBounds();
      const currentKey = this.monthKey(year, month);
      const prevDisabled = currentKey <= bounds.min;
      const nextDisabled = currentKey >= bounds.max;

      const firstWeekday = new Date(year, month, 1).getDay();
      const daysInMonth = new Date(year, month + 1, 0).getDate();

      // Position day 1 with grid-column-start (instead of leading empty <div>s, which a
      // global `div:empty { display:none }` would collapse and shift the whole grid).
      let cells = '';
      for (let dayNum = 1; dayNum <= daysInMonth; dayNum += 1) {
        const iso = toISO(year, month, dayNum);
        const entry = this.dates.get(iso);
        const isSelected = iso === this.selectedDate;
        const colStart = dayNum === 1 ? ` style="grid-column-start: ${firstWeekday + 1};"` : '';

        if (entry && this.isWithinMax(iso)) {
          const ariaLabel = formatLong(entry.day);
          const tabindex =
            isSelected || (!this.selectedDate && iso === this.selectableList[0]) ? '0' : '-1';
          cells +=
            `<button type="button" class="cart-delivery-date__cell cart-delivery-date__cell--available` +
            `${isSelected ? ' is-selected' : ''}" data-delivery-day="${iso}" aria-label="${ariaLabel}"` +
            `${isSelected ? ' aria-pressed="true"' : ''} tabindex="${tabindex}"${colStart}>${dayNum}</button>`;
        } else {
          cells +=
            `<div class="cart-delivery-date__cell cart-delivery-date__cell--disabled" aria-disabled="true"${colStart}>${dayNum}</div>`;
        }
      }

      const weekdayHeader = WEEKDAYS.map(
        (d) => `<div class="cart-delivery-date__weekday">${d}</div>`
      ).join('');

      this.calendarEl.innerHTML =
        `<div class="cart-delivery-date__nav">` +
        `<button type="button" class="cart-delivery-date__arrow" data-delivery-action="prev"` +
        `${prevDisabled ? ' disabled' : ''} aria-label="Previous month">&#8249;</button>` +
        `<span class="cart-delivery-date__month" aria-live="polite">${MONTHS[month]} ${year}</span>` +
        `<button type="button" class="cart-delivery-date__arrow" data-delivery-action="next"` +
        `${nextDisabled ? ' disabled' : ''} aria-label="Next month">&#8250;</button>` +
        `</div>` +
        `<div class="cart-delivery-date__grid" role="grid">` +
        `<div class="cart-delivery-date__weekdays" role="row">${weekdayHeader}</div>` +
        `<div class="cart-delivery-date__days">${cells}</div>` +
        `</div>` +
        (this.selectableList.length
          ? ''
          : `<p class="cart-delivery-date__empty">No delivery dates are currently available.</p>`);
    }

    /* ---------------------------------------------------------------- events */

    onClick(event) {
      if (this.mode === 'modal' && event.target.closest('[data-delivery-close], [data-delivery-overlay]')) {
        this.close();
        return;
      }

      const action = event.target.closest('[data-delivery-action]');
      if (action) {
        const type = action.dataset.deliveryAction;
        if (type === 'toggle') {
          this.expanded = !this.expanded;
          this.render();
        } else if (type === 'prev' && !action.disabled) {
          this.shiftMonth(-1);
        } else if (type === 'next' && !action.disabled) {
          this.shiftMonth(1);
        }
        return;
      }

      const dayButton = event.target.closest('[data-delivery-day]');
      if (dayButton) this.selectDate(dayButton.dataset.deliveryDay);
    }

    onKeydown(event) {
      if (this.mode === 'modal' && event.key === 'Escape') {
        this.close();
        return;
      }

      const dayButton = event.target.closest('[data-delivery-day]');
      if (!dayButton) return;
      const current = dayButton.dataset.deliveryDay;

      if (event.key === 'Home') {
        event.preventDefault();
        this.focusDate(this.selectableList[0]);
        return;
      }
      if (event.key === 'End') {
        event.preventDefault();
        this.focusDate(this.selectableList[this.selectableList.length - 1]);
        return;
      }

      const deltas = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
      if (!(event.key in deltas)) return;

      event.preventDefault();
      const idx = this.selectableList.indexOf(current);
      const next = idx + deltas[event.key];
      if (next >= 0 && next < this.selectableList.length) this.focusDate(this.selectableList[next]);
    }

    shiftMonth(delta) {
      const { year, month } = this.viewMonth;
      const d = new Date(year, month + delta, 1);
      this.viewMonth = { year: d.getFullYear(), month: d.getMonth() };
      this.renderCalendar();
    }

    focusDate(iso) {
      if (!iso) return;
      const day = this.dates.get(iso).day;
      const key = this.monthKey(day.getFullYear(), day.getMonth());
      if (key !== this.monthKey(this.viewMonth.year, this.viewMonth.month)) {
        this.viewMonth = { year: day.getFullYear(), month: day.getMonth() };
        this.renderCalendar();
      }
      const btn = this.calendarEl.querySelector(`[data-delivery-day="${iso}"]`);
      if (btn) {
        this.calendarEl
          .querySelectorAll('[data-delivery-day]')
          .forEach((b) => b.setAttribute('tabindex', '-1'));
        btn.setAttribute('tabindex', '0');
        btn.focus();
      }
    }

    /* ------------------------------------------------------------ selection */

    selectDate(iso) {
      const sel = select(iso); // writes localStorage synchronously
      if (!sel) return;

      // Optimistic UI: reflect the choice immediately. select() already persisted the
      // selection to localStorage, so the line stamp on add (which reads localStorage)
      // is correct even if the network writes below are still in flight.
      this.selectedDate = iso;
      this.dataset.selectedDate = iso;
      this.dataset.selectedLabel = sel.label;

      document.dispatchEvent(
        new CustomEvent('delivery-date:changed', { detail: { date: iso, label: sel.label } })
      );

      if (this.mode === 'modal') {
        this.close(sel);
      } else {
        this.expanded = false;
        this.render();
        this.syncCheckoutButton();
      }

      // Persist to the cart in the background (cart attributes + re-stamp line items).
      this.persistSelection(sel);
    }

    async persistSelection(sel) {
      try {
        await setCartAttributes(sel);
      } catch (error) {
        console.error('cart-delivery-date: failed to persist selection', error);
      }
    }

    // Checkout has two independent gates: a chosen delivery date (this component) AND a
    // valid zip (cart-zip-validator). Each owns a data-attribute marker; the button is
    // enabled only when neither gate is blocking — so the two don't fight over `disabled`.
    syncCheckoutButton() {
      document
        .querySelectorAll('#CartDrawer-Checkout, #checkout, .cart__checkout-button')
        .forEach((btn) => {
          btn.toggleAttribute('data-needs-delivery-date', !this.selectedDate || !this.isWithinMax(this.selectedDate));
          const blocked =
            btn.hasAttribute('data-needs-zip') || btn.hasAttribute('data-needs-delivery-date');
          btn.disabled = blocked;
          btn.setAttribute('aria-disabled', blocked ? 'true' : 'false');
        });
    }
  }

  if (!customElements.get('cart-delivery-date')) {
    customElements.define('cart-delivery-date', CartDeliveryDate);
  }
})();
